import debug from "src/modules/debug-logger";
import * as actions from "src/modules/activity-log/duck/actions";
import {
  isVideoEvent,
  isPhotoEvent
} from "src/modules/activity-log/supported-events";
import { store } from "src/store/store";
import { getActivityLogUploader } from "src/modules/activity-log/lib/ActivityLogUploader";
import getTimeoutForAttempt from "src/modules/activity-log/lib/ActivityLogUploader/getTimeoutForAttempt";
import uploadMedia from "./uploadBrowserMedia";

export default class BrowserUploader {
  dispatch = store.dispatch;
  audioUploadQueue = [];
  captureUploadQueue = [];
  liveUploads = {};
  getResultForFiles = {};
  audioUploadInProgress = null;
  captureUploadInProgress = null;
  originalUploader = getActivityLogUploader();

  uploadFinishedMonitoring = async ({
    monitoring,
    lastEvent,
    isFreeVersion
  }) => {
    if (lastEvent.eventType === "AWAKE" && !isFreeVersion) {
      const fileRelatedLength = await this.finishAudioUpload({
        fileName: lastEvent.fileName
      });
      lastEvent.fileRelatedLength = fileRelatedLength;
    }

    await this.originalUploader.uploadFinishedMonitoring({
      monitoring,
      lastEvent,
      isFreeVersion
    });
  };

  uploadCaptureEvent = (
    { fileName, monitoringId, onSample, eventType, rawData },
    getResult
  ) => {
    debug.activityLog(`uploadCaptureEvent called for fileName ${fileName}`);
    const media = {
      fileName,
      monitoringId,
      onSample,
      attempt: 1,
      type: this.mapEventTypeToMediaType(eventType),
      rawData
    };
    if (getResult) {
      this.getResultForFiles[fileName] = getResult;
    }
    this.handleUpload(media);
  };

  uploadAudioEvent = ({ fileName, monitoringId, onSample, eventType }) => {
    debug.activityLog(`uploadAudioEvent called for fileName ${fileName}`);

    const media = {
      audioData: [],
      fileName,
      monitoringId,
      onSample,
      attempt: 1,
      finishUpload: false,
      type: this.mapEventTypeToMediaType(eventType)
    };
    media.onData = (data) => {
      media.audioData.push(data);
    };
    this.liveUploads[fileName] = media;

    this.handleUpload(media);
  };

  finishAudioUpload = ({ fileName }) => {
    return new Promise((resolve) => {
      const media = this.liveUploads[fileName];
      media.finishUpload = true;
      delete this.liveUploads[fileName];
      resolve(media.fileRelatedLength);
    });
  };

  onData = ({ data, fileName }) => {
    const media = this.liveUploads[fileName];
    if (media == null) return;
    media.onData(data);
  };

  handleUpload = async (media) => {
    if (media.type === "AUDIO") {
      if (this.audioUploadInProgress) {
        this.liveUploads[media.fileName]
          ? this.audioUploadQueue.unshift(media)
          : this.audioUploadQueue.push(media);
        return;
      }
      this.audioUploadInProgress = media;
    } else {
      if (this.captureUploadInProgress) {
        this.liveUploads[media.fileName]
          ? this.captureUploadQueue.unshift(media)
          : this.captureUploadQueue.push(media);
        return;
      }
      this.captureUploadInProgress = media;
    }

    const startNextUpload = () => {
      if (media.type === "AUDIO") {
        this.audioUploadInProgress = null;
        const nextUpload = this.audioUploadQueue.shift();
        if (nextUpload) this.handleUpload(nextUpload);
      } else {
        this.captureUploadInProgress = null;
        const nextUpload = this.captureUploadQueue.shift();
        if (nextUpload) this.handleUpload(nextUpload);
      }
    };

    const runUpload = async () => {
      const getResult = this.getResultForFiles[media.fileName];
      try {
        await uploadMedia(media);
        debug.activityLog("File uploaded, fileName", media.fileName);
        if (getResult) getResult({ result: "SUCCESS" });
        this.dispatch(
          actions.eventMediaUploaded({
            fileName: media.fileName,
            monitoringId: media.monitoringId
          })
        );
        startNextUpload();
      } catch (error) {
        if (getResult) getResult({ result: "FAILURE" });
        // eslint-disable-next-line no-console
        console.error(
          `media upload attempt ${media?.attempt} failed`,
          error || "no error provided"
        );
        if (media.attempt >= 5 && media.finishedWritting) {
          media.attempt = 1;
          media.type === "AUDIO"
            ? this.audioUploadQueue.push(media)
            : this.captureUploadQueue.push(media);
          startNextUpload();
          return;
        }
        if (navigator.onLine) {
          ++media.attempt;
          await new Promise((resolve) =>
            setTimeout(resolve, getTimeoutForAttempt(media.attempt))
          );
        } else {
          await new Promise((resolve) => {
            media.attempt = 1;
            window.addEventListener("online", resolve, { once: true });
          });
        }
        runUpload();
      }
      if (getResult) {
        delete this.getResultForFiles[media.fileName];
      }
    };
    runUpload();
  };

  mapEventTypeToMediaType = (eventType) => {
    if (isVideoEvent(eventType)) return "VIDEO";
    if (isPhotoEvent(eventType)) return "PHOTO";
    return "AUDIO";
  };
}
